import { apiClient } from './client';
import type { BarcodeLookupSettings, BCOrderStatus, Currency, MerchantSettings, OrderStatusSettings, PickupMethod, TaxClass,TaxRate, TaxZone } from './types';

export const settingsApi = {
  async get(): Promise<MerchantSettings> {
    return apiClient.request<MerchantSettings>('/settings');
  },

  async update(data: {
    regions?: string[];
    receiptTemplate?: {
      logoUrl?: string;
      businessName?: string;
      headerText?: string;
      footerText?: string;
      showBarcode?: boolean;
      showItemizedTax?: boolean;
    };
    receiptOptions?: {
      emailEnabled?: boolean;
      smsEnabled?: boolean;
      printEnabled?: boolean;
    };
    businessName?: string;
    contactEmail?: string;
    contactPhone?: string;
    taxDisplayInclusive?: boolean;
    barcodeLookupSettings?: BarcodeLookupSettings;
    orderStatusSettings?: OrderStatusSettings;
    permissionSettings?: {
      escalationMode: 'required' | 'optional' | 'disabled';
    };
  }): Promise<{ success: boolean }> {
    return apiClient.request<{ success: boolean }>('/settings', {
      method: 'PUT',
      body: data,
    });
  },

  async getOrderStatuses(): Promise<{ statuses: BCOrderStatus[] }> {
    return apiClient.request<{ statuses: BCOrderStatus[] }>('/settings/order-statuses');
  },

  async getPickupMethods(): Promise<{ methods: PickupMethod[] }> {
    return apiClient.request<{ methods: PickupMethod[] }>('/pickup-methods');
  },

  async createPickupMethod(data: {
    locationId: number;
    displayName?: string;
    collectionInstructions?: string;
    collectionTimeDescription?: string;
  }): Promise<{ method: PickupMethod; created: boolean }> {
    return apiClient.request<{ method: PickupMethod; created: boolean }>('/pickup-methods', {
      method: 'POST',
      body: data,
    });
  },

  async autoSetupPickupMethods(): Promise<{
    created: Array<{ locationId: number; locationName: string; pickupMethodId: number }>;
    message: string;
  }> {
    return apiClient.request('/pickup-methods/auto-setup', {
      method: 'POST',
    });
  },

  async getCurrencies(): Promise<{ currencies: Currency[] }> {
    return apiClient.request<{ currencies: Currency[] }>('/settings/currencies');
  },

  async getTaxZones(): Promise<{ zones: TaxZone[] }> {
    return apiClient.request<{ zones: TaxZone[] }>('/tax/zones');
  },

  async getTaxRates(taxZoneId?: number): Promise<{ rates: TaxRate[] }> {
    const query = taxZoneId ? `?tax_zone_id=${taxZoneId}` : '';
    return apiClient.request<{ rates: TaxRate[] }>(`/tax/rates${query}`);
  },

  async getTaxClasses(): Promise<{ classes: TaxClass[] }> {
    return apiClient.request<{ classes: TaxClass[] }>('/tax/classes');
  },

  async getTaxSync(): Promise<{
    classes: Array<{ id: number; name: string }>;
    zones: TaxZone[];
    rates: TaxRate[];
    settings: {
      tax_entered_with_prices: boolean;
      store_tax_zone_id: number;
    };
    syncedAt: string;
  }> {
    return apiClient.request('/tax/sync');
  },

  async getChannels(): Promise<{
    channels: Array<{
      id: number;
      name: string;
      type: string;
      platform: string;
      status: string;
      isEnabled: boolean;
      isListable: boolean;
      dateCreated: string;
    }>;
    currentChannelId: number;
  }> {
    return apiClient.request('/settings/channels');
  },

  async updateChannel(channelId: number): Promise<{
    updated: boolean;
    channelId: number;
    channelName: string;
  }> {
    return apiClient.request('/settings/channel', {
      method: 'PUT',
      body: { channelId },
    });
  },
};
